import { useInViewPort } from 'hooks/useInViewport';
import { TextField, Tooltip } from 'uikit';
import type { TextFieldProps, TooltipProps, TooltipPropSize } from 'uikit';
import { useOnRefAttributeChange } from 'utils/dom';

import { useCallback, useRef } from 'react';
import type { Control, UseControllerProps } from 'react-hook-form';
import { useController } from 'react-hook-form';

type ControlledTextFieldProps = TextFieldProps<'text' | 'number' | 'textarea'> &
    Omit<UseControllerProps<any>, 'control'> & {
        directionTooltip?: TooltipProps['direction'];
        classNameTooltip?: string;
        tooltipSize?: TooltipPropSize;
        isTooltipHidden?: boolean;
        checkIntersectionVisibility?: boolean;
        control?: Control<any, any>;
        onTooltipAttributeChange?: (attributeName: string, target: Node) => void;
        intersectionObserverOptions?: IntersectionObserverInit;
    };
export function ControlledTextField({
    name,
    control,
    rules,
    directionTooltip = 'rightCenter',
    classNameTooltip,
    tooltipSize = 's',
    isTooltipHidden,
    labelPosition = 'left',
    onTooltipAttributeChange,
    placeholder = 'Введите',
    checkIntersectionVisibility = true,
    intersectionObserverOptions,
    ...props
}: ControlledTextFieldProps) {
    const { field, fieldState } = useController({
        name,
        control,
        rules,
    });

    const handleOnChange = useCallback(
        ({ value }) => {
            field.onChange(value);
        },
        [field],
    );

    const ref = useRef(null);
    const tooltipRef = useRef<HTMLDivElement>(null);

    useOnRefAttributeChange({
        targetRef: tooltipRef,
        onAttributeChange: onTooltipAttributeChange,
    });

    const isElementVisible = useInViewPort(ref, checkIntersectionVisibility, intersectionObserverOptions);

    return (
        <>
            <TextField
                value={String(field.value ?? '')}
                onChange={handleOnChange}
                labelPosition={labelPosition}
                width="full"
                size="s"
                status={fieldState.invalid ? 'alert' : undefined}
                inputContainerRef={ref}
                placeholder={placeholder}
                {...props}
            />

            {isElementVisible && !isTooltipHidden && fieldState.invalid && fieldState.error?.message && (
                <Tooltip
                    size={tooltipSize}
                    direction={directionTooltip}
                    possibleDirections={[directionTooltip]}
                    status="alert"
                    anchorRef={ref}
                    className={`${classNameTooltip} tooltip-${name}`}
                    ref={tooltipRef}
                >
                    {fieldState.error?.message}
                </Tooltip>
            )}
        </>
    );
}

ControlledTextField.displayName = 'ControlledTextField';
